import {
  useCreateFolderMutation,
  useGetArchivedWorkspaceFoldersQuery,
  useGetCurrentUserQuery,
  useGetWorkspaceFoldersQuery,
  useGetWorkspaceQuery,
} from '@carboncopyco/data';
import { FolderAddIcon, PlusOutlineIcon } from '@carboncopyco/icons';
import type { Role, WorkspaceFolder } from '@carboncopyco/types';
import {
  Button,
  TextBase,
  TextPageHeader,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { useFolderAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useRef } from 'react';
import { WorkspaceAvatar } from '..';
import { useModalActions } from '../../utils';
import { FolderModal } from './Folders';
import { WorkspacePageBody } from './WorkspacePageBody';
import { useWorkspacePageProps } from './WorkspacePageContext';
import { WorkspacePageCreateMenu } from './WorkspacePageCreateMenu';
import { WorkspacePageDropdown } from './WorkspacePageDropdown';
import { WorkspacePageHeader } from './WorkspacePageHeader';

const workspaceTypeTitle = {
  public: 'Public workspace',
  private: 'Private workspace',
  default: 'Default workspace',
  draft: 'Draft workspace',
};

export const WorkspaceActiveContent = () => {
  const { trackFolderClickCreate } = useFolderAnalytics('new_folder_modal');
  const folderButtonRef = useRef<HTMLDivElement>(null);
  const { modalActionsByName } = useModalActions();
  const newFolderModal = modalActionsByName('new-folder-modal');
  const { workspaceId, organizationId } = useWorkspacePageProps();

  const { data: workspace } = useGetWorkspaceQuery({
    organizationId: organizationId,
    id: workspaceId,
  });
  const { data: userData } = useGetCurrentUserQuery();
  const userRole: Role = userData?.user?.roles?.[organizationId];
  const [isLoading, setIsLoading] = useBoolean();
  const { data: activeFolders } = useGetWorkspaceFoldersQuery({
    organizationId,
    workspaceId,
  });

  const { data: archivedFolders } = useGetArchivedWorkspaceFoldersQuery({
    organizationId,
    workspaceId,
  });
  const activeFoldersList = activeFolders?.map((folder) => folder?.name);

  const archivedFoldersList = archivedFolders?.map((folder) => folder?.name);

  const [createFolder] = useCreateFolderMutation();

  const onHandleFolderSubmit = (folderName: WorkspaceFolder['name']) => {
    trackFolderClickCreate({
      workspaceId,
      workspaceName: workspace?.workspaceName,
      workspaceType: workspace?.workspaceType,
    });
    setIsLoading.setTrue();
    createFolder({
      organizationId,
      workspaceId,
      name: folderName,
    })
      .unwrap()
      .catch(() =>
        notify.error('Unable to create the folder. Please try again.')
      )
      .finally(() => {
        setIsLoading.setFalse();
        newFolderModal.closeModal();
      });
  };

  return (
    <>
      <WorkspacePageHeader>
        <>
          <div className="flex items-center">
            <div className="p-0.5 bg-ds-accent3-100 rounded drop-shadow-xl">
              {workspace && (
                <WorkspaceAvatar
                  workspace={workspace}
                  size="xlarge"
                />
              )}
            </div>
            <div className="flex flex-col pl-6 ">
              <TooltipBubble
                title={workspace?.workspaceName}
                placement="top-start"
              >
                <div className="max-w-[700px] mr-10 truncate">
                  <TextPageHeader
                    label={workspace?.workspaceName}
                    color="text-ds-natural-700"
                    ellipsis
                  />
                </div>
              </TooltipBubble>
              <TextBase
                label={workspace && workspaceTypeTitle[workspace.workspaceType]}
                color="text-ds-natural-500"
              />
            </div>
          </div>
          <div className="flex items-center">
            <WorkspacePageDropdown />
            {userRole !== 'presenter' && (
              <>
                <TooltipBubble
                  placement="top"
                  title="New folder"
                >
                  <div
                    className="px-4"
                    ref={folderButtonRef}
                  >
                    <Button
                      icon={<FolderAddIcon />}
                      onClick={newFolderModal.openModal}
                      secondary
                      aria-label="add folder"
                    />
                  </div>
                </TooltipBubble>

                <WorkspacePageCreateMenu
                  asChild
                  triggerElement={
                    <Button
                      label="Create new"
                      leftIcon={<PlusOutlineIcon />}
                    />
                  }
                  location="workspace_page"
                />
              </>
            )}
          </div>
        </>
      </WorkspacePageHeader>
      <WorkspacePageBody />
      {newFolderModal.isModalOpen() && (
        <FolderModal
          show={newFolderModal.isModalOpen()}
          onClose={newFolderModal.closeModal}
          activeFoldersNames={activeFoldersList}
          archivedFoldersNames={archivedFoldersList}
          onHandleFolderSubmit={onHandleFolderSubmit}
          isLoading={isLoading}
          isCreateNew
        />
      )}
    </>
  );
};
